import { useEffect, useRef } from "react";

/**
 * TradingHorizonHeroBg
 * Ultra-premium cinematic hero background inspired by TradingView's iconic Earth Horizon:
 * - Curved planet horizon with radiant atmospheric sunrise flare & deep space starfield
 * - Real-time 60 FPS institutional candlesticks marching smoothly along the horizon curve
 * - Glowing neon price wave with dynamic wicks, volume aura & pulsing trade beacon
 * - Vertical institutional satellite beams rising into the night sky
 * - Hardware-accelerated Canvas with retina DPR scaling
 */
const TradingHorizonHeroBg = ({
  className = "",
  showBeams = true,
  candleSpeed = 0.7,
  overlayOpacity = 0.6,
  children
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId;
    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Mouse parallax
    let targetMouseX = 0;
    let targetMouseY = 0;
    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      targetMouseX = x * 30;
      targetMouseY = y * 15;
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    // Candle data generator
    const candleSpacing = 28;
    const candles = [];
    let currentPrice = 100;

    const createCandle = (x) => {
      const change = (Math.random() - 0.48) * 14;
      const open = currentPrice;
      const close = open + change;
      const high = Math.max(open, close) + Math.random() * 8;
      const low = Math.min(open, close) - Math.random() * 8;
      currentPrice = Math.max(50, Math.min(150, close));
      const isBullish = close >= open;

      return {
        x,
        open,
        close,
        high,
        low,
        isBullish,
        alpha: Math.random() * 0.4 + 0.6
      };
    };

    const resize = () => {
      if (!canvas || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);

      // Re-populate candles across full width with buffer
      candles.length = 0;
      currentPrice = 100;
      const count = Math.ceil(width / candleSpacing) + 8;
      for (let i = -4; i < count; i++) {
        candles.push(createCandle(i * candleSpacing));
      }
    };

    resize();
    window.addEventListener("resize", resize);

    // Beams shooting upward
    const beams = [
      { xRatio: 0.22, height: 260, phase: 0, speed: 0.02 },
      { xRatio: 0.48, height: 320, phase: 1.5, speed: 0.015 },
      { xRatio: 0.72, height: 240, phase: 3.0, speed: 0.025 },
      { xRatio: 0.88, height: 200, phase: 4.2, speed: 0.018 }
    ];

    let time = 0;

    // Horizon arc equation
    const getHorizonY = (x) => {
      // Gentle curve across bottom half
      const normalizedX = (x - width / 2) / (width / 2);
      const arc = normalizedX * normalizedX * 45; // parabolic drop at edges
      const baseY = height * 0.72; // sits at ~72% height
      return baseY + arc + mouseY;
    };

    const render = () => {
      time += 0.016;
      // Smooth mouse parallax
      mouseX += (targetMouseX - mouseX) * 0.05;
      mouseY += (targetMouseY - mouseY) * 0.05;

      ctx.clearRect(0, 0, width, height);

      // ─── 1. ATMOSPHERIC SUNRISE GLOW ───
      const sunX = width / 2 + mouseX * 0.5;
      const sunY = getHorizonY(sunX) - 15;

      // Radial burst
      const sunGlow = ctx.createRadialGradient(sunX, sunY, 10, sunX, sunY, Math.max(width * 0.45, 400));
      sunGlow.addColorStop(0, "rgba(255, 255, 255, 0.45)");
      sunGlow.addColorStop(0.12, "rgba(0, 255, 136, 0.28)");
      sunGlow.addColorStop(0.35, "rgba(0, 240, 255, 0.12)");
      sunGlow.addColorStop(0.7, "rgba(168, 85, 247, 0.03)");
      sunGlow.addColorStop(1, "transparent");

      ctx.fillStyle = sunGlow;
      ctx.beginPath();
      ctx.arc(sunX, sunY, Math.max(width * 0.45, 400), 0, Math.PI * 2);
      ctx.fill();

      // Atmospheric Horizon Arc Line
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = "rgba(0, 255, 136, 0.45)";
      ctx.lineWidth = 2.5;
      ctx.shadowColor = "#00FF88";
      ctx.shadowBlur = 18;

      for (let x = 0; x <= width; x += 10) {
        const y = getHorizonY(x);
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();

      // ─── 2. SATELLITE BEAMS ───
      if (showBeams) {
        beams.forEach((beam) => {
          const bx = width * beam.xRatio + mouseX * 0.3;
          const by = getHorizonY(bx);
          const beamPulse = (Math.sin(time * 3 + beam.phase) + 1) * 0.5;
          const beamH = beam.height * (0.8 + beamPulse * 0.35);

          const grad = ctx.createLinearGradient(bx, by, bx, by - beamH);
          grad.addColorStop(0, "rgba(0, 255, 136, 0.6)");
          grad.addColorStop(0.2, "rgba(0, 240, 255, 0.35)");
          grad.addColorStop(1, "transparent");

          ctx.save();
          ctx.strokeStyle = grad;
          ctx.lineWidth = 1.5;
          ctx.shadowColor = "#00F0FF";
          ctx.shadowBlur = 10;
          ctx.beginPath();
          ctx.moveTo(bx, by);
          ctx.lineTo(bx, by - beamH);
          ctx.stroke();

          // Pulse ring at beam base
          ctx.fillStyle = "rgba(0, 255, 136, 0.8)";
          ctx.beginPath();
          ctx.arc(bx, by, 2.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        });
      }

      // ─── 3. ADVANCE CANDLESTICKS ───
      for (let i = 0; i < candles.length; i++) {
        candles[i].x += candleSpeed;
      }

      // Wrap around when candle leaves right edge
      if (candles.length > 0 && candles[candles.length - 1].x > width + candleSpacing * 2) {
        // Pop off and place new candle on left
        const newCandle = createCandle(candles[0].x - candleSpacing);
        candles.pop();
        candles.unshift(newCandle);
      }

      // ─── 4. DRAW GLOWING PRICE LINE CONNECTING CANDLES ───
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = "rgba(0, 255, 136, 0.75)";
      ctx.lineWidth = 2;
      ctx.shadowColor = "#00FF88";
      ctx.shadowBlur = 12;

      let firstPoint = true;
      let lastCandle = null;

      for (let i = 0; i < candles.length; i++) {
        const c = candles[i];
        if (c.x < -candleSpacing || c.x > width + candleSpacing) continue;
        const horizonY = getHorizonY(c.x);
        // Vertical price offset mapped onto horizon
        const candleCenterY = horizonY - 45 - (c.close - 100) * 1.6;

        if (firstPoint) {
          ctx.moveTo(c.x, candleCenterY);
          firstPoint = false;
        } else {
          ctx.lineTo(c.x, candleCenterY);
        }
        lastCandle = { x: c.x, y: candleCenterY, isBullish: c.isBullish, close: c.close };
      }
      ctx.stroke();
      ctx.restore();

      // ─── 5. DRAW CANDLESTICKS ───
      for (let i = 0; i < candles.length; i++) {
        const c = candles[i];
        if (c.x < -candleSpacing || c.x > width + candleSpacing) continue;

        const horizonY = getHorizonY(c.x);
        const candleBaseY = horizonY - 45;
        const openY = candleBaseY - (c.open - 100) * 1.6;
        const closeY = candleBaseY - (c.close - 100) * 1.6;
        const highY = candleBaseY - (c.high - 100) * 1.6;
        const lowY = candleBaseY - (c.low - 100) * 1.6;

        const bodyTop = Math.min(openY, closeY);
        const bodyHeight = Math.max(Math.abs(closeY - openY), 3);
        const color = c.isBullish ? "#00FF88" : "#FF3B69";
        const bodyWidth = 8;

        ctx.save();
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = c.isBullish ? 8 : 6;

        // Wick (high-low)
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(c.x, highY);
        ctx.lineTo(c.x, lowY);
        ctx.stroke();

        // Candle Body
        ctx.fillRect(c.x - bodyWidth / 2, bodyTop, bodyWidth, bodyHeight);
        ctx.restore();
      }

      // ─── 6. LEADING PRICE BEACON (PULSE NODE) ───
      if (lastCandle) {
        const pulse = (Math.sin(time * 6) + 1) * 0.5;
        const radius = 4 + pulse * 2;

        ctx.save();
        // Outer ripple
        ctx.strokeStyle = "rgba(0, 255, 136, 0.4)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(lastCandle.x, lastCandle.y, radius * 3.5, 0, Math.PI * 2);
        ctx.stroke();

        // Center glowing orb
        ctx.fillStyle = "#FFFFFF";
        ctx.shadowColor = "#00FF88";
        ctx.shadowBlur = 20;
        ctx.beginPath();
        ctx.arc(lastCandle.x, lastCandle.y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      animId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [candleSpeed, showBeams]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden bg-[#05070E] ${className}`}
      data-testid="trading-horizon-hero"
    >
      {/* 8K Photorealistic Earth Horizon & Space Backdrop */}
      <div
        className="absolute inset-0 bg-cover bg-center pointer-events-none transform scale-[1.02] transition-transform duration-1000 ease-out"
        style={{
          backgroundImage: "url('/trading-earth-horizon.jpg')",
          backgroundPosition: "center 55%",
        }}
      />

      {/* Atmospheric Vignette & Contrast Gradients for Content Legibility */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 20%, rgba(5,7,14,0.45) 0%, rgba(5,7,14,${overlayOpacity + 0.15}) 70%, #05070E 100%), linear-gradient(180deg, #05070E 0%, transparent 22%, transparent 75%, #05070E 100%)`,
        }}
      />

      {/* 60 FPS Real-time Candlesticks & Horizon Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-10"
        style={{ opacity: 0.95 }}
      />

      {/* Subtle Star Twinkle Grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40 z-0"
        style={{
          backgroundImage: "radial-gradient(1px 1px at 20px 30px, #ffffff, rgba(0,0,0,0)), radial-gradient(1.5px 1.5px at 140px 70px, rgba(0,255,136,0.8), rgba(0,0,0,0)), radial-gradient(1px 1px at 280px 180px, #00f0ff, rgba(0,0,0,0))",
          backgroundSize: "320px 320px",
        }}
      />

      {/* Foreground Content */}
      <div className="relative z-20 w-full">
        {children}
      </div>
    </div>
  );
};

export default TradingHorizonHeroBg;
